import _ from "lodash";

import { AxiosResponse } from "axios";
import { BaseEntity, IResponseResult } from "@/services/data/base_types";
import base_ds from "@/services/base_datasource_service";
import request from "@/utils/request";
import {FacilityType, OkHnRow} from "@/services/models-service";
import {convertPeriodsListToMonthArray} from "@/utils/periods";
import balance from "@/views/balance/index.vue";
import {route_transport} from "@/services/transport-cost-input-service";


export const route_ns = `balance`;

const BALANCE_PRESET_CACHE_KEY = 'balance_preset'

export enum Grouping {
  BY_PERIOD = "by_period",
  BY_FACILITY = "by_facility"
}

export const groupingDataSource = [
  {
    name: Grouping.BY_PERIOD,
    text: 'Группировка по периодам'
  },
  {
    name: Grouping.BY_FACILITY,
    text: 'Группировка по объектам'
  },
]

const PAO_T_PLUS_ID = -1;

export const totalCaptionObject = {
  id: PAO_T_PLUS_ID,
  name: 'ПАО "Т Плюс"',
  weight: '1'
}

export class BalanceRowData {
  ok: number = 0;
  hn: number = 0;
  purchase_vendors: number = 0;
  loss_total: number = 0;
  tp: number = 0;
  tso_p: number = 0;
  kp: number = 0;
  useful: number = 0;
}

export class BalanceRow extends BaseEntity {
  id_eco_heat_node: number = 0;
  id_eco_facility: number = 0;
  month: string = "";
  steam: BalanceRowData | null = null;
  system_water: BalanceRowData | null = null;
  imbalance: number = 0;
  facility_name?: string;
  imbalance_steam: number = 0;
  imbalance_water: number = 0;
}

export class BalancePreset extends BaseEntity {
  id_user: string | null = null; // Текущий пользователь.
  grouping: Grouping = Grouping.BY_PERIOD;
  id_eco_filials: number[] = []; // Филиалы
  id_eco_heat_nodes: number[] = []; // Выбранные тепловые узлы
  id_eco_facilities: number[] = []; // Выбранные объекты тепловых узлов
  id_year: string | null = null; // id Выбранного года
  id_cut: string | null = null;  // Основная срезка
  id_compare_cut: string | null = null; // Срезка для сравнения
  period: any = null; // Выбранный период
  show_deviation: boolean = false; // Показать отклонение
  show_pao_tplus: boolean = true; // Показать суммарно по всем тепловым узлам компании
  show_selected_nodes_facility: boolean = true; // Объекты выбранных тепловых узлов
  show_cut: boolean = true; // Показать основную срезку
  show_compare_cut: boolean = false; // Показать срезку для сравнения
  show_correlation: boolean = false; // Показать стоимостные корректировки
}

class BalanceRowAgg extends BaseEntity {
  id_object: number = 0;
  month: string = "";
  imbalance: number = 0;
  imbalance_steam: number = 0;
  imbalance_water: number = 0;
  steam: BalanceRowData = new BalanceRowData();
  system_water: BalanceRowData = new BalanceRowData();
  facility_name?: string;
}

class GroupedBalanceRow extends BaseEntity {
  id: string = '';
  title: string = '';
  month: string = "";
  steam: BalanceRowData | null = null;
  system_water: BalanceRowData | null = null;
  imbalance: number = 0;
  imbalance_steam: number = 0;
  imbalance_water: number = 0;
}

const balanceRowDifference = (red: BalanceRowData | null, sub: BalanceRowData | null): BalanceRowData => {
  let res = new BalanceRowData();
  for (let x of (Object.keys(res) as Array<keyof BalanceRowData>)) {
    res[x] = (red ? red[x] : 0) - (sub ? sub[x] : 0);
  }
  return res;
}

const balanceRowSumma = (red: BalanceRowData | null, sub: BalanceRowData | null): BalanceRowData => {
  let res = new BalanceRowData();
  for (let x of (Object.keys(res) as Array<keyof BalanceRowData>)) {
    res[x] = (red ? red[x] : 0) + (sub ? sub[x] : 0);
  }
  return res;
}

const addBalanceRowToAgg = (aggRow: BalanceRowAgg, balanceRow: BalanceRow): BalanceRowAgg => {
  if (balanceRow.steam) {
    for (let x of Object.keys(balanceRow.steam)) {
      (aggRow as any).steam[x] = ((aggRow as any).steam[x] ?? 0) + (balanceRow.steam[x as keyof BalanceRowData] ?? 0);
    }
  }
  if (balanceRow.system_water) {
    for (let x of Object.keys(balanceRow.system_water)) {
      (aggRow as any).system_water[x] = ((aggRow as any).system_water[x] ?? 0) + (balanceRow.system_water[x as keyof BalanceRowData] ?? 0);
    }
  }
  aggRow.imbalance = (aggRow.imbalance ?? 0) + (balanceRow.imbalance ?? 0);
  aggRow.imbalance_steam = (aggRow.imbalance_steam ?? 0) + (balanceRow.imbalance_steam ?? 0);
  aggRow.imbalance_water = (aggRow.imbalance_water ?? 0) + (balanceRow.imbalance_water ?? 0);
  return aggRow;
};


const aggregateByObjects = (balanceArray: BalanceRow[], calcFacilities = false) => {
  let facilityAgg: BalanceRowAgg[] = [];
  let nodeAgg: BalanceRowAgg[] = [];
  let totalAgg: BalanceRowAgg[] = [];
  balanceArray.forEach(x => {
    const existsNodeIdx = nodeAgg.findIndex(aggItem => x.id_eco_heat_node === aggItem.id_object && x.month === aggItem.month);
    if (existsNodeIdx === -1) {
      const newAggRow = new BalanceRowAgg();
      newAggRow.month = x.month;
      newAggRow.id_object = x.id_eco_heat_node;
      nodeAgg.push(addBalanceRowToAgg(newAggRow, x));
    } else {
      nodeAgg[existsNodeIdx] = addBalanceRowToAgg(nodeAgg[existsNodeIdx], x);
    }
    const existsTotalIdx = totalAgg.findIndex(aggItem => x.month === aggItem.month);
    if (existsTotalIdx === -1) {
      const newAggRow = new BalanceRowAgg();
      newAggRow.month = x.month;
      newAggRow.id_object = PAO_T_PLUS_ID;
      totalAgg.push(addBalanceRowToAgg(newAggRow, x));
    } else {
      totalAgg[existsTotalIdx] = addBalanceRowToAgg(totalAgg[existsTotalIdx], x);
    }
    if (calcFacilities) {
      const newAggRow = new BalanceRowAgg();
      newAggRow.month = x.month;
      newAggRow.id_object = x.id_eco_facility;
      newAggRow.facility_name = x.facility_name;
      facilityAgg.push(addBalanceRowToAgg(newAggRow, x));
    }
  });
  return [ ...totalAgg, ...nodeAgg, ...facilityAgg ];

}

const calculateBalanceNodesOnly = (balanceArray: BalanceRow[]): BalanceRowAgg[] => {
  let result:  BalanceRowAgg[] = [];

  balanceArray.forEach(balanceRow => {
    const existsNodeIdx = result.findIndex(r => r.id_object === balanceRow.id_eco_heat_node && r.month === balanceRow.month);
    if (existsNodeIdx === -1) {
      const newNodeRow = new BalanceRowAgg();
      newNodeRow.id_object = balanceRow.id_eco_heat_node;
      newNodeRow.month = balanceRow.month;
      result.push(addBalanceRowToAgg(newNodeRow, balanceRow));
    } else {
      result[existsNodeIdx] = addBalanceRowToAgg(result[existsNodeIdx],  balanceRow)
    }
  })

  return result;
}

const calculateYearBalanceByPeriod = (balanceArray: BalanceRow[]): BalanceRow[] => {
  const result: BalanceRow[] = [];
  balanceArray.forEach(balanceRow => {
    const existsNodeIdx = result.findIndex(r => r.id_eco_facility === balanceRow.id_eco_facility);
    if (existsNodeIdx === -1) {
      result.push({...balanceRow, month: 'year'});
    } else {
      result[existsNodeIdx].steam = balanceRowSumma(result[existsNodeIdx].steam,  balanceRow.steam);
      result[existsNodeIdx].steam = balanceRowSumma(result[existsNodeIdx].system_water,  balanceRow.system_water);
      result[existsNodeIdx].imbalance += balanceRow.imbalance;
    }
  });

  return result;

}

class BalanceService {
    public crud = base_ds.getBaseCrud(route_ns);
    public data_source = base_ds.getBaseDataSource(route_ns);

    public current: BalancePreset = new BalancePreset();

    public balance: BalanceRow[] = [];

    public balanceNodes: BalanceRowAgg[] = [];

    public balanceByObj: BalanceRowAgg[] = [];

    public balanceCompare: BalanceRow[] = [];

    public balanceCompareNodes: BalanceRowAgg[] = [];

    public balanceCompareByObj: BalanceRowAgg[] = [];

    public deviationData: BalanceRow[] | BalanceRowAgg[] = [];

    public isBalanceLoading = false;

    public allowExcelExport = false;

    public selectedGroupedObject: string[] = [];

    public groupedBalanceObj: GroupedBalanceRow[] = [];

    private saveToCache() {
      localStorage.setItem(BALANCE_PRESET_CACHE_KEY, JSON.stringify(this.current));
    }

    public loadFromCache() {
      const cachedPreset = localStorage.getItem(BALANCE_PRESET_CACHE_KEY);
      if (cachedPreset) {
        try {
          this.current = JSON.parse(cachedPreset) as BalancePreset;
        } catch (e) {
          console.log('Ошибка загрузки пресета Баланса из локального хранилища: ' + e);
        }
      }
    }

    public setCurrent(value: BalancePreset): void {
      this.current = _.cloneDeep(value);
      this.current.show_selected_nodes_facility = true
      this.saveToCache();
    }

    // Запрос данных срезки для сравнения, если пользователь указал флаг "НЕ показывать срезку для сравнения", вернуть false
    private async requestCompareBalance(filterStr: string) {
      if (this.current.id_cut && this.current.show_compare_cut && this.current.id_compare_cut) {
        const resp = await this.requestBalance(filterStr.replace(this.current.id_cut, this.current.id_compare_cut))
        if (resp) {
          this.balanceCompare = resp;
          return true;
        };
      }
      return false
    }

    public getWithFilter(): void {
    this.balance = [];
    this.balanceCompare = [];
    this.deviationData = [];

    if (!this.current.id_cut || !this.current.period.length) {
        console.log('Не заданы срезка или период');
        return;
    }

    let filter: any[] = [
        ["id_cut", "=", this.current.id_cut],
        ["month", "in", convertPeriodsListToMonthArray(this.current.period)]
    ];

    if (!this.current.show_pao_tplus) {
        // ✅ ИСПРАВЛЕНО: учитываем групповые объекты в условии
        if (!this.current.id_eco_heat_nodes.length && 
            !this.current.show_selected_nodes_facility && 
            !this.selectedGroupedObject.length) {
              filter.push(["heat_node_id", "in", this.current.id_eco_heat_nodes]);
            console.log('Не выбраны филиалы, узлы, объекты или групповые объекты');
            return;
        }

        if (this.current.id_eco_heat_nodes.length) {
            filter.push(["heat_node_id", "in", this.current.id_eco_heat_nodes]);
        }
        
    }

    this.isBalanceLoading = true;
    let filterStr = JSON.stringify(filter);

    Promise.all([
        // ✅ Загружаем основные данные только если есть обычные объекты
        (this.current.show_pao_tplus || this.current.id_eco_heat_nodes.length || this.current.show_selected_nodes_facility) 
            ? this.requestBalance(filterStr) 
            : Promise.resolve([]),
        // ✅ Загружаем данные для сравнения только если есть обычные объекты
        (this.current.show_pao_tplus || this.current.id_eco_heat_nodes.length || this.current.show_selected_nodes_facility) 
            ? this.requestCompareBalance(filterStr) 
            : Promise.resolve(false),
        // ✅ Всегда загружаем групповые объекты если они выбраны
        this.selectedGroupedObject.length > 0 
            ? this.getBalanceGroupedWithFilter(filterStr) 
            : Promise.resolve([])
    ]).then(resp => {
        // Обрабатываем основные данные
        this.balance = resp[0] || [];

        if (this.balance.length > 0) {
            if (this.current.grouping === Grouping.BY_FACILITY) {
                this.balanceByObj = aggregateByObjects(this.balance, this.current.show_selected_nodes_facility);
            } else if (!this.current.show_selected_nodes_facility) {
                this.balanceNodes = calculateBalanceNodesOnly(this.balance);
            }
        }

        // Обрабатываем данные сравнения
        const balanceCompare = resp[1];
        // if (hasCompareData) {
        //     if (this.current.grouping === Grouping.BY_FACILITY) {
        //         this.balanceCompareByObj = aggregateByObjects(this.balanceCompare, this.current.show_selected_nodes_facility);
        //     } else if (!this.current.show_selected_nodes_facility) {
        //         this.balanceCompareNodes = calculateBalanceNodesOnly(this.balanceCompare);
        //     }

        //     if (this.current.show_deviation) {
        //         if (!this.current.show_selected_nodes_facility && this.current.grouping === Grouping.BY_PERIOD) {
        //             this.calcDeviationNode();
        //         } else {
        //             this.calcDeviation();
        //         }
        //     }
        // }

        // Обрабатываем групповые объекты
        if (resp[2].length > 0) {
            this.groupedBalanceObj = resp[2];
        }

        setTimeout(() => {
            this.isBalanceLoading = false;
            // ✅ Разрешаем экспорт если есть любые данные
            this.allowExcelExport = this.balance.length > 0 || this.groupedBalanceObj.length > 0;
        }, 200);
    });
}


    private calcDeviation() {
      let result: BalanceRow[] = [];
      let compareFindIndexList: number[] = [];
      this.balance.forEach( b => {
        const existsIdx = this.balanceCompare.findIndex(x => x.id_eco_facility === b.id_eco_facility && x.month === b.month);
        if (existsIdx === -1) {
          result.push(b);
        } else {
          compareFindIndexList.push(existsIdx);
          result.push( {
            ...b,
            steam: balanceRowDifference(b.steam, this.balanceCompare[existsIdx].steam),
            system_water: balanceRowDifference(b.system_water, this.balanceCompare[existsIdx].system_water),
            imbalance: b.imbalance - this.balanceCompare[existsIdx].imbalance,
          } );
        }
      });
      if (this.balanceCompare.length !== compareFindIndexList.length) {
        for (let i = 0; i < this.balanceCompare.length; ++i) {
          if (!compareFindIndexList.includes(i)) {
            const b = new BalanceRow();
            result.push({
              ...this.balanceCompare[i],
              steam: balanceRowDifference(b.steam, this.balanceCompare[i].steam),
              system_water: balanceRowDifference(b.system_water, this.balanceCompare[i].system_water),
              imbalance: b.imbalance - this.balanceCompare[i].imbalance,
            });
          }
        }
      }
      if (this.current.grouping === Grouping.BY_FACILITY) {
        this.deviationData = aggregateByObjects(result, this.current.show_selected_nodes_facility);
      } else {
        this.deviationData = result;
      }
    }

  private calcDeviationNode() {
    let result: BalanceRowAgg[] = [];
    let compareFindIndexList: number[] = [];
    this.balanceNodes.forEach( b => {
      const existsIdx = this.balanceCompareNodes.findIndex(x => x.id_object === b.id_object && x.month === b.month);
      if (existsIdx === -1) {
        result.push(b);
      } else {
        compareFindIndexList.push(existsIdx);
        result.push( {
          ...b,
          steam: balanceRowDifference(b.steam, this.balanceCompareNodes[existsIdx].steam),
          system_water: balanceRowDifference(b.system_water, this.balanceCompareNodes[existsIdx].system_water),
          imbalance: b.imbalance - this.balanceCompareNodes[existsIdx].imbalance,
        } );
      }
    });
    if (this.balanceCompareNodes.length !== compareFindIndexList.length) {
      for (let i = 0; i < this.balanceCompareNodes.length; ++i) {
        if (!compareFindIndexList.includes(i)) {
          const b = new BalanceRowAgg();
          result.push({
            ...this.balanceCompareNodes[i],
            steam: balanceRowDifference(b.steam, this.balanceCompareNodes[i].steam),
            system_water: balanceRowDifference(b.system_water, this.balanceCompareNodes[i].system_water),
            imbalance: b.imbalance - this.balanceCompareNodes[i].imbalance,
          });
        }
      }
    }
    this.deviationData = result;
  }

    private async requestBalance(filter: string): Promise<BalanceRow[]> {
      const response: AxiosResponse["data"] = await request({
        url: route_ns,
        method: "get",
        params: { filter }
      });
      if(response && response.data) {
        return response.data;
      }
      return [];
    }

  public async getCoastBalanceWithFilter(
    id_cut: string | null,
    heat_node_ids: number[],
    periods: string[]
  ) {

    let filter: any[] = []
    if(this.current.show_pao_tplus){
      filter = [
      ["id_cut", "=", id_cut],
      "and",
      ["month", "in", convertPeriodsListToMonthArray(periods)],
    ];
  } else {
    
      filter = [
      ["id_cut", "=", id_cut],
      "and",
      ["heat_node_id", "in", heat_node_ids],
      "and",
      ["month", "in", convertPeriodsListToMonthArray(periods)],
    ];
  }
    const url = `${route_ns}/cost_balance?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    const { data } = await request({
      url,
      method: "get",
    });

    return data ?? [];
  }

  public async getCoastBalanceGroupedWithFilter(
    id_cut: string | null,
    heat_node_ids: number[],
    periods: string[],
    grouped: string[],
  ) {

    const filter = [
      ["id_cut", "=", id_cut],
      "and",
      ["heat_node_id", "in", heat_node_ids],
      "and",
      ["month", "in", convertPeriodsListToMonthArray(periods)],
    ];
    const url = `${route_ns}/cost_balance/grouped?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    const { data } = await request({
      url,
      method: "post",
      data: {object_ids: grouped},
    });

    return data ?? [];
  }

  public async getBalanceGroupedWithFilter(filter: string) {
    const url = `${route_ns}/grouped?filter=${filter}`;
    const { data } = await request({
      url,
      method: "post",
      data: {object_ids: this.selectedGroupedObject},
    });
    return data ?? [];
  }
}

export default new BalanceService();
